import { combineReducers } from 'redux';
import { activityTypeConfigApi } from './stores/activity-types';
import {
  adminDaToolPortCallsApi,
  adminDaToolPortCallsSlice,
  adminProfilesApi,
  adminProfilesTableSlice,
  adminSupportBuyerApi,
  buyerPaymentInstructionsApi,
  buyerPaymentInstructionsTableSlice,
  buyerProfileApi,
  buyerTeamsApi,
  buyerTeamsTableSlice,
  charterersTableSlice,
  chartererVesselTypesTableSlice,
  kypApi,
  kypVerificationsApi,
  kypVerificationsTableSlice,
  portsApi,
  portTerminalsTableSlice,
  qaAdminSupportApi
} from './stores/admin';
import { adminAccountingCodesApi } from './stores/admin-accounting-codes';
import { adminRegionsApi, adminRegionsSlice } from './stores/admin-regions';
import { adminRulesApi, adminRulesSlice } from './stores/admin-rules';
import { formulaExecutionApi } from './stores/admin/port-analysis/formula-execution/api';
import {
  agentAccountReducer,
  agentAccountStateName
} from './stores/agent-account';
import { appointmentPreviewResponseApi } from './stores/agent-appointment-preview';
import { agentBankAccountsApi } from './stores/agent-bank-accounts';
import { agentRatingApi } from './stores/agent-rating';
import {
  appointmentApi,
  appointmentReducerName,
  appointmentsReducer
} from './stores/appointment';
import { assignmentApi } from './stores/assignment/api';
import { bankCodesApi } from './stores/bank-codes';

import { portCallApi } from '@state/stores/port-call';
import { requestForQuotationApi } from '@state/stores/request-for-quotation';
import { daExportApi } from './stores/admin/integrations/daExportApi';
import { buyerDaToolRequestWizardApi } from './stores/buyer-da-tool-request-wizard/api';
import { buyerDashboardSlice, portCallsApi } from './stores/buyer-dashboard';
import { paymentInstructionsApi } from './stores/buyer-payment-instructions';
import cache from './stores/cache';
import { charterersApi } from './stores/charterers';
import { conversationApi, conversationsSlice } from './stores/conversation';
import { daAppBaseGraphqlApi } from './stores/da-app/daAppBaseGraphqlApi';
import { danaosApi } from './stores/danaos';
import { evaluationApi, evaluationSelectionsSlice } from './stores/evaluation';
import { evaluationPageSlice } from './stores/evaluation/reducers';
import { expenseEvaluationInspectionApi } from './stores/expense-evaluation-inspection';
import { financialPaymentsApi } from './stores/financial-payments';
import {
  bankAccountDocumentScannerApi,
  documentRecogniserApi,
  expenseEvaluationDaHelperApi,
  expenseEvaluationDocumentTranslationApi,
  expenseEvaluationFormulaApi,
  harborlabAiApi,
  sofEventsExtractorApi
} from './stores/harborlab-ai';
import { husbandryCostEstimatorApi } from './stores/husbandry-cost-estimator/husbandryCostEstimator';
import { integrationsApi } from './stores/integrations';
import { logEntriesApi } from './stores/log-entries';
import { metadataSlice } from './stores/metadata';
import { notificationsApi, notificationsSlice } from './stores/notifications';
import { portAnalysisApi } from './stores/port-analysis-service/portAnalysisServiceApi';
import { portSimulationConfigurationApi } from './stores/port-simulation-configuration/port-simulation-configuration-api';
import { portCostEstimatorApi } from './stores/port-cost-estimator';
import { portDirectoryHarborComplexFiltersSlice } from './stores/port-directory-harbox-complex-filters/reducers';
import { portlogApi } from './stores/port-log';
import {
  publicRfQPageApi,
  sanitizedRfQPageSlice
} from './stores/publicRfQPage';
import { refSplitApi, secureRefSplitApi, secureSplitApi } from './stores/ref';
import { mainRestApi } from './stores/restApi/rest-api';
import { sofBaseGraphqlApi } from './stores/statement-of-facts/graphql/sofBaseGraphqlApi';
import {
  sofRestApi,
  sofRestAttachmentsApi
} from './stores/statement-of-facts/rest';
import { sessionReducer, sessionStateName } from './stores/user';
import { vesselApi } from './stores/vessel';
import { voyageApi, voyageReducer, voyageStateName } from './stores/voyage';
import { accountPickerApi } from './stores/login-account-picker';
import { supplierMainDetailsApi } from '@state/stores/admin/support/supplier/main-details';
import { supplierLegalEntityDetailsApi } from '@state/stores/admin/support/supplier/legal-entity-details';
import { evaluationV2Api } from './stores/evaluation-v2/api';
import { proformaV2Api } from './stores/proforma-v2/api';
import { appointmentV2Api } from './stores/appointment-v2/api';

const reducers = {
  [mainRestApi.reducerPath]: mainRestApi.reducer,
  [voyageStateName]: voyageReducer,
  [sessionStateName]: sessionReducer,
  [appointmentReducerName]: appointmentsReducer,
  [agentAccountStateName]: agentAccountReducer,
  [evaluationSelectionsSlice.name]: evaluationSelectionsSlice.reducer,
  cache,
  [buyerDashboardSlice.name]: buyerDashboardSlice.reducer,
  [portCallsApi.reducerPath]: portCallsApi.reducer,
  [agentRatingApi.reducerPath]: agentRatingApi.reducer,
  [adminRegionsApi.reducerPath]: adminRegionsApi.reducer,
  [adminRegionsSlice.name]: adminRegionsSlice.reducer,
  [adminRulesApi.reducerPath]: adminRulesApi.reducer,
  [adminRulesSlice.name]: adminRulesSlice.reducer,
  [refSplitApi.reducerPath]: refSplitApi.reducer,
  [secureRefSplitApi.reducerPath]: secureRefSplitApi.reducer,
  [secureSplitApi.reducerPath]: secureSplitApi.reducer,
  [agentBankAccountsApi.reducerPath]: agentBankAccountsApi.reducer,
  [financialPaymentsApi.reducerPath]: financialPaymentsApi.reducer,
  [activityTypeConfigApi.reducerPath]: activityTypeConfigApi.reducer,
  [adminAccountingCodesApi.reducerPath]: adminAccountingCodesApi.reducer,
  [logEntriesApi.reducerPath]: logEntriesApi.reducer,
  [conversationApi.reducerPath]: conversationApi.reducer,
  [conversationsSlice.name]: conversationsSlice.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [notificationsSlice.name]: notificationsSlice.reducer,
  [evaluationApi.reducerPath]: evaluationApi.reducer,
  [evaluationV2Api.reducerPath]: evaluationV2Api.reducer,
  [proformaV2Api.reducerPath]: proformaV2Api.reducer,
  [appointmentV2Api.reducerPath]: appointmentV2Api.reducer,
  [evaluationPageSlice.name]: evaluationPageSlice.reducer,
  [adminProfilesApi.reducerPath]: adminProfilesApi.reducer,
  [adminProfilesTableSlice.name]: adminProfilesTableSlice.reducer,
  [expenseEvaluationInspectionApi.reducerPath]:
    expenseEvaluationInspectionApi.reducer,
  [expenseEvaluationFormulaApi.reducerPath]:
    expenseEvaluationFormulaApi.reducer,
  [expenseEvaluationDaHelperApi.reducerPath]:
    expenseEvaluationDaHelperApi.reducer,
  [expenseEvaluationDocumentTranslationApi.reducerPath]:
    expenseEvaluationDocumentTranslationApi.reducer,
  [buyerTeamsApi.reducerPath]: buyerTeamsApi.reducer,
  [buyerTeamsTableSlice.name]: buyerTeamsTableSlice.reducer,
  [paymentInstructionsApi.reducerPath]: paymentInstructionsApi.reducer,
  [adminSupportBuyerApi.reducerPath]: adminSupportBuyerApi.reducer,
  [appointmentPreviewResponseApi.reducerPath]:
    appointmentPreviewResponseApi.reducer,
  [buyerPaymentInstructionsTableSlice.name]:
    buyerPaymentInstructionsTableSlice.reducer,
  [buyerPaymentInstructionsApi.reducerPath]:
    buyerPaymentInstructionsApi.reducer,
  [portsApi.reducerPath]: portsApi.reducer,
  [portTerminalsTableSlice.name]: portTerminalsTableSlice.reducer,
  [kypVerificationsTableSlice.name]: kypVerificationsTableSlice.reducer,
  [kypVerificationsApi.reducerPath]: kypVerificationsApi.reducer,
  [publicRfQPageApi.reducerPath]: publicRfQPageApi.reducer,
  [sanitizedRfQPageSlice.name]: sanitizedRfQPageSlice.reducer,
  [bankCodesApi.reducerPath]: bankCodesApi.reducer,
  [kypApi.reducerPath]: kypApi.reducer,
  [adminDaToolPortCallsApi.reducerPath]: adminDaToolPortCallsApi.reducer,
  [adminDaToolPortCallsSlice.name]: adminDaToolPortCallsSlice.reducer,
  [assignmentApi.reducerPath]: assignmentApi.reducer,
  [portlogApi.reducerPath]: portlogApi.reducer,
  [appointmentApi.reducerPath]: appointmentApi.reducer,
  [metadataSlice.name]: metadataSlice.reducer,
  [vesselApi.reducerPath]: vesselApi.reducer,
  [voyageApi.reducerPath]: voyageApi.reducer,
  [formulaExecutionApi.reducerPath]: formulaExecutionApi.reducer,
  [charterersApi.reducerPath]: charterersApi.reducer,
  [integrationsApi.reducerPath]: integrationsApi.reducer,
  [buyerProfileApi.reducerPath]: buyerProfileApi.reducer,
  [charterersTableSlice.name]: charterersTableSlice.reducer,
  [chartererVesselTypesTableSlice.name]: chartererVesselTypesTableSlice.reducer,
  [portCostEstimatorApi.reducerPath]: portCostEstimatorApi.reducer,
  [sofBaseGraphqlApi.reducerPath]: sofBaseGraphqlApi.reducer,
  [daAppBaseGraphqlApi.reducerPath]: daAppBaseGraphqlApi.reducer,
  [sofRestApi.reducerPath]: sofRestApi.reducer,
  [sofRestAttachmentsApi.reducerPath]: sofRestAttachmentsApi.reducer,
  [buyerDaToolRequestWizardApi.reducerPath]:
    buyerDaToolRequestWizardApi.reducer,
  [requestForQuotationApi.reducerPath]: requestForQuotationApi.reducer,
  [portCallApi.reducerPath]: portCallApi.reducer,
  [portAnalysisApi.reducerPath]: portAnalysisApi.reducer,
  [daExportApi.reducerPath]: daExportApi.reducer,
  [danaosApi.reducerPath]: danaosApi.reducer,
  [harborlabAiApi.reducerPath]: harborlabAiApi.reducer,
  [bankAccountDocumentScannerApi.reducerPath]:
    bankAccountDocumentScannerApi.reducer,
  [documentRecogniserApi.reducerPath]: documentRecogniserApi.reducer,
  [sofEventsExtractorApi.reducerPath]: sofEventsExtractorApi.reducer,
  [portDirectoryHarborComplexFiltersSlice.name]:
    portDirectoryHarborComplexFiltersSlice.reducer,
  [husbandryCostEstimatorApi.reducerPath]: husbandryCostEstimatorApi.reducer,
  [portSimulationConfigurationApi.reducerPath]:
    portSimulationConfigurationApi.reducer,
  [qaAdminSupportApi.reducerPath]: qaAdminSupportApi.reducer,
  [supplierMainDetailsApi.reducerPath]: supplierMainDetailsApi.reducer,
  [supplierLegalEntityDetailsApi.reducerPath]:
    supplierLegalEntityDetailsApi.reducer,
  [accountPickerApi.reducerPath]: accountPickerApi.reducer
};

export const middlewares = [
  mainRestApi.middleware,
  portCallsApi.middleware,
  agentRatingApi.middleware,
  adminRegionsApi.middleware,
  adminRulesApi.middleware,
  refSplitApi.middleware,
  secureRefSplitApi.middleware,
  secureSplitApi.middleware,
  agentBankAccountsApi.middleware,
  financialPaymentsApi.middleware,
  adminAccountingCodesApi.middleware,
  logEntriesApi.middleware,
  conversationApi.middleware,
  notificationsApi.middleware,
  evaluationApi.middleware,
  evaluationV2Api.middleware,
  proformaV2Api.middleware,
  appointmentV2Api.middleware,
  adminProfilesApi.middleware,
  buyerTeamsApi.middleware,
  adminSupportBuyerApi.middleware,
  expenseEvaluationInspectionApi.middleware,
  expenseEvaluationFormulaApi.middleware,
  expenseEvaluationDaHelperApi.middleware,
  expenseEvaluationDocumentTranslationApi.middleware,
  appointmentPreviewResponseApi.middleware,
  paymentInstructionsApi.middleware,
  buyerPaymentInstructionsApi.middleware,
  portsApi.middleware,
  kypVerificationsApi.middleware,
  publicRfQPageApi.middleware,
  bankCodesApi.middleware,
  kypApi.middleware,
  adminDaToolPortCallsApi.middleware,
  activityTypeConfigApi.middleware,
  appointmentApi.middleware,
  assignmentApi.middleware,
  portlogApi.middleware,
  vesselApi.middleware,
  voyageApi.middleware,
  formulaExecutionApi.middleware,
  charterersApi.middleware,
  integrationsApi.middleware,
  husbandryCostEstimatorApi.middleware,
  buyerProfileApi.middleware,
  portCostEstimatorApi.middleware,
  sofBaseGraphqlApi.middleware,
  daAppBaseGraphqlApi.middleware,
  buyerDaToolRequestWizardApi.middleware,
  sofRestApi.middleware,
  sofRestAttachmentsApi.middleware,
  requestForQuotationApi.middleware,
  portCallApi.middleware,
  portAnalysisApi.middleware,
  daExportApi.middleware,
  danaosApi.middleware,
  harborlabAiApi.middleware,
  bankAccountDocumentScannerApi.middleware,
  documentRecogniserApi.middleware,
  sofEventsExtractorApi.middleware,
  portSimulationConfigurationApi.middleware,
  qaAdminSupportApi.middleware,
  supplierMainDetailsApi.middleware,
  supplierLegalEntityDetailsApi.middleware,
  accountPickerApi.middleware
];

export const rootReducer = combineReducers(reducers);
